import { startTransition, useCallback, useMemo, useState } from 'react'

/** Text scale steps, in percent. 200% is the WCAG 2.2 § 1.4.4 resize ceiling. */
export const READING_SCALES = [100, 125, 150, 200] as const

export type ReadingScale = (typeof READING_SCALES)[number]

export const MIN_READING_SCALE: ReadingScale = 100
export const MAX_READING_SCALE: ReadingScale = 200
export const DEFAULT_READING_SCALE: ReadingScale = MIN_READING_SCALE

/**
 * Exhaustive transition table keyed by the scale union.
 * A table beats index arithmetic here: lookups are O(1), the ends clamp by
 * construction, and there is no unreachable bounds branch to leave untested.
 */
const READING_SCALE_TRANSITIONS = {
  100: { up: 125, down: 100 },
  125: { up: 150, down: 100 },
  150: { up: 200, down: 125 },
  200: { up: 200, down: 150 },
} as const satisfies Record<ReadingScale, { readonly up: ReadingScale; readonly down: ReadingScale }>

export interface ReadingScaleState {
  readonly scale: ReadingScale
  readonly canIncrease: boolean
  readonly canDecrease: boolean
  readonly increase: () => void
  readonly decrease: () => void
}

/**
 * Session-scoped reading-comfort control for poem pages.
 *
 * Nothing is persisted — no storage, no cookies, no server state — which keeps
 * the app stateless end to end and keeps server and client markup identical on
 * first paint (CLS stays at 0.0, hydration never mismatches).
 *
 * Updates are wrapped in `startTransition` so a scale change can never block an
 * in-flight interaction (INP budget, rules/architecture.md).
 */
export function useReadingScale(
  initialScale: ReadingScale = DEFAULT_READING_SCALE,
): ReadingScaleState {
  const [scale, setScale] = useState<ReadingScale>(initialScale)

  const increase = useCallback(() => {
    startTransition(() => {
      setScale((current) => READING_SCALE_TRANSITIONS[current].up)
    })
  }, [])

  const decrease = useCallback(() => {
    startTransition(() => {
      setScale((current) => READING_SCALE_TRANSITIONS[current].down)
    })
  }, [])

  return useMemo(
    () => ({
      scale,
      canIncrease: scale !== MAX_READING_SCALE,
      canDecrease: scale !== MIN_READING_SCALE,
      increase,
      decrease,
    }),
    [scale, increase, decrease],
  )
}
